import type { Currency, NavMonthlyPoint } from '@nav/shared';

import { CountUp } from '@/components/react-bits/CountUp';
import { SpotlightCard } from '@/components/react-bits/SpotlightCard';
import { Card } from '@/components/ui/Card';
import { EmptyState, ErrorState, LoadingRegion, Skeleton } from '@/components/ui/states';
import { EM_DASH, formatNav, formatPct, formatPctPlain, trendOf } from '@/lib/format';
import { BRAND } from '../palette';

function KpiCell({
  label,
  numeric,
  format,
  delta,
  dir,
}: {
  label: string;
  numeric: number | null;
  format: (value: number) => string;
  delta: string;
  dir: 'up' | 'down' | 'flat';
}) {
  const deltaColor = dir === 'up' ? BRAND.mint : dir === 'down' ? BRAND.coral : BRAND.textDim;
  return (
    <SpotlightCard className="bg-white h-full">
      <div className="px-6 py-[22px]">
        <p
          className="uppercase mb-2.5"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 10.5,
            letterSpacing: '1.5px',
            color: BRAND.textFaint,
          }}
        >
          {label}
        </p>
        <p
          className="tabular-nums"
          style={{ fontSize: 27, color: BRAND.ink, fontFamily: 'var(--font-display)', lineHeight: 1.15 }}
        >
          {numeric == null ? EM_DASH : <CountUp to={numeric} format={format} duration={2} />}
        </p>
        <p className="mt-1.5" style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, color: deltaColor }}>
          {delta}
        </p>
      </div>
    </SpotlightCard>
  );
}

function dirOf(current: number | null, previous: number | null): 'up' | 'down' | 'flat' {
  if (current == null || previous == null) return 'flat';
  if (current > previous) return 'up';
  if (current < previous) return 'down';
  return 'flat';
}

/**
 * Four KPI cells driven by the full monthly series of the selected product.
 * Values are ink, not gold. Direction colour lives only on the delta line.
 */
export function SflKpiRow({
  points,
  currency,
  isLoading,
  error,
  onRetry,
}: {
  points: NavMonthlyPoint[];
  currency: Currency;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  if (isLoading) {
    return (
      <div className="sfl-kpi-row">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white px-6 py-[22px]">
            <LoadingRegion label="Memuat KPI" className="block">
              <Skeleton height={10} width="40%" radius={4} />
              <div style={{ height: 12 }} />
              <Skeleton height={28} width="70%" radius={6} />
              <div style={{ height: 10 }} />
              <Skeleton height={12} width="55%" radius={4} />
            </LoadingRegion>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <ErrorState error={error} onRetry={onRetry} compact />
      </Card>
    );
  }

  if (points.length === 0) {
    return (
      <Card>
        <EmptyState message="Belum ada data NAV untuk produk ini, jadi KPI masih kosong." compact />
      </Card>
    );
  }

  const latest = points[points.length - 1]!;
  const prev = points.length >= 2 ? points[points.length - 2]! : null;
  const first = points[0]!;
  const totalGrowth =
    first.navClose !== 0 ? ((latest.navClose - first.navClose) / first.navClose) * 100 : null;
  const navDelta = prev ? latest.navClose - prev.navClose : null;
  const months = points.length;

  const navDir = dirOf(latest.navClose, prev?.navClose ?? null);
  const momDir = dirOf(latest.momPct, prev?.momPct ?? null);
  const yoyDir = dirOf(latest.yoyPct, prev?.yoyPct ?? null);
  const totalDir = trendOf(totalGrowth) === 'down' ? 'down' : trendOf(totalGrowth) === 'up' ? 'up' : 'flat';

  const navDeltaLabel =
    navDelta == null
      ? EM_DASH
      : `${navDelta >= 0 ? '+' : ''}${formatNav(navDelta, currency)} dari bulan lalu`;

  const momDeltaLabel =
    latest.momPct == null || prev?.momPct == null
      ? prev
        ? 'vs bulan lalu'
        : EM_DASH
      : `${latest.momPct >= prev.momPct ? '▲' : '▼'} vs ${formatPctPlain(prev.momPct)} bulan lalu`;

  const yoyDeltaLabel =
    latest.yoyPct == null || prev?.yoyPct == null
      ? prev
        ? 'vs bulan lalu'
        : EM_DASH
      : `${latest.yoyPct >= prev.yoyPct ? '▲' : '▼'} vs ${formatPctPlain(prev.yoyPct)} bulan lalu`;

  return (
    <div className="sfl-kpi-row">
      <KpiCell
        label="NAV Terakhir"
        numeric={latest.navClose}
        format={(n) => formatNav(n, currency)}
        delta={navDeltaLabel}
        dir={navDir}
      />
      <KpiCell
        label="Growth Bulanan"
        numeric={latest.momPct}
        format={formatPctPlain}
        delta={momDeltaLabel}
        dir={momDir}
      />
      <KpiCell
        label="YOY Terkini"
        numeric={latest.yoyPct}
        format={formatPctPlain}
        delta={yoyDeltaLabel}
        dir={yoyDir}
      />
      <KpiCell
        label={`Total Growth (${months} Bln)`}
        numeric={totalGrowth}
        format={formatPct}
        delta={`Dari ${formatNav(first.navClose, currency)} ke ${formatNav(latest.navClose, currency)}`}
        dir={totalDir}
      />
    </div>
  );
}
